import React, { useState } from 'react';
import BrandSymbol from './BrandSymbol';

interface ProgressCheckModalProps {
  isOpen: boolean;
  onClose: () => void;
  completionPercent: number;
  userName: string;
  onFeeling?: (feeling: 'ready' | 'overwhelmed') => void;
}

const ProgressCheckModal: React.FC<ProgressCheckModalProps> = ({
  isOpen,
  onClose,
  completionPercent,
  userName,
  onFeeling
}) => {
  const [feeling, setFeeling] = useState<'ready' | 'overwhelmed' | null>(null);

  if (!isOpen) return null;

  const handleFinish = () => {
    if (feeling && onFeeling) {
      onFeeling(feeling);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <div className="w-full max-w-[1130px] bg-stone-50 rounded-md shadow-[0_0_10px_4px_rgba(49,49,33,0.25)] p-10 relative animate-fadeIn">
        {/* Close button */}
        <button
          aria-label="Close modal"
          onClick={onClose}
          className="absolute top-6 right-6 w-6 h-6 flex items-center justify-center group"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-stone-500 group-hover:text-stone-700">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>

        {/* Heading & Progress */}
        <div className="flex flex-col items-center mb-8">
          <BrandSymbol size="3xl" variant="dark" showText={true} className="mb-4" />
          <h2 className="font-display text-lg font-semibold text-stone-800 text-center">{userName ? `${userName}'s Estate Plan` : 'Your Estate Plan'}</h2>
          <div className="flex items-center gap-2 mt-1">
            <div className="w-3.5 h-3.5 bg-zinc-300 rounded-[10px] relative">
              <div className="absolute w-1.5 h-1.5 bg-green-700 rotate-90 origin-top-left left-1 top-1"></div>
            </div>
            <p className="text-xs font-medium text-slate-400">{completionPercent}% complete</p>
          </div>
        </div>

        {/* Success message */}
        <p className="text-green-700 font-display text-2xl font-semibold text-center leading-relaxed max-w-[570px] mx-auto">
          Nicely done. Your estate plan is already&nbsp;{completionPercent}% complete.
        </p>

        {/* Feeling question */}
        <h3 className="mt-8 mb-4 font-sans text-lg font-medium text-stone-800 text-center">How are you feeling?</h3>
        <div className="flex flex-col items-center gap-4 mb-10">
          {[
            {
              id: 'ready',
              label: 'Doing well - ready to jump in'
            },
            {
              id: 'overwhelmed',
              label: "Feeling overwhelmed - let's start small"
            }
          ].map(option => (
            <button
              key={option.id}
              onClick={() => setFeeling(option.id as 'ready' | 'overwhelmed')}
              className={`w-full max-w-[499px] h-10 rounded outline outline-1 outline-offset-[-0.5px] flex items-center px-6 gap-3 transition-colors ${
                feeling === option.id
                  ? 'bg-green-50 outline-green-700'
                  : 'bg-stone-100 outline-stone-400 hover:bg-stone-200'
              }`}
            >
              <span className={`w-4 h-4 rounded-full border flex-shrink-0 ${
                feeling === option.id ? 'border-green-700' : 'border-stone-400'
              } flex items-center justify-center`}>
                {feeling === option.id && (
                  <span className="w-3 h-3 bg-green-700 rounded-full" />
                )}
              </span>
              <span className="font-sans text-base font-normal text-stone-800">{option.label}</span>
            </button>
          ))}
        </div>

        {/* Finish button */}
        <div className="flex justify-center">
          <button
            disabled={!feeling}
            onClick={handleFinish}
            className={`px-6 py-3 rounded bg-cyan-700 text-stone-50 font-medium flex items-center gap-2 hover:bg-cyan-800 transition-colors ${
              !feeling ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            Continue <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProgressCheckModal;
